export type EnvironmentType = 'permanent' | 'ephemeral' | 'local' | 'sandbox';

export type EnvironmentItem = {
  id: string;
  propertyId: string;
  name: string;
  environmentKindId: string | null;
  environmentKindName: string | null;
  url: string;
  type: EnvironmentType;
  isDefault: boolean;
  expiresAt: string | null;
  description: string | null;
  customerAwsRoleArn: string | null;
  customerSubnetId: string | null;
  customerSecurityGroupId: string | null;
  customerRegion: string | null;
  /** Decrypted values; sensitive ones arrive masked as `........` */
  envVarValues?: Record<string, string>;
  /** Per-env override of the property's relative login path */
  loginPath: string | null;
  /** Backing template for type='sandbox' environments (v1). Null for v2 environments. */
  sandboxTemplateId: string | null;
  /** Definition environment name (cofactor.sandbox.yaml) — set only on Sandboxes v2 environments synced from the application's definition. */
  sandboxV2Environment: string | null;
  /**
   * Name of the sandbox template backing this environment (server-derived via
   * the backing instance). Present for transient `type='ephemeral'` rows so we
   * can label them "Instance of <template>"; null otherwise.
   */
  templateName: string | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
};

type TypeBadgeColor = 'orange' | 'gray' | 'purple' | 'teal';

export const ENVIRONMENT_TYPE_BADGE: Record<
  EnvironmentType,
  { label: string; color: TypeBadgeColor }
> = {
  permanent: { label: 'Permanent', color: 'teal' },
  ephemeral: { label: 'Ephemeral', color: 'orange' },
  local: { label: 'Local', color: 'gray' },
  sandbox: { label: 'Sandbox', color: 'purple' },
};

export const SANDBOX_STATUS_BADGE: Record<
  string,
  { label: string; color: 'green' | 'blue' | 'gray' | 'red' | 'yellow' }
> = {
  ready: { label: 'Running', color: 'green' },
  warming: { label: 'Warming', color: 'blue' },
  stopping: { label: 'Stopping', color: 'yellow' },
  asleep: { label: 'Asleep', color: 'gray' },
  failed: { label: 'Failed', color: 'red' },
};

export function getEnvironmentTypeBadge(type: EnvironmentType) {
  return ENVIRONMENT_TYPE_BADGE[type];
}

/**
 * Descriptor for a transient per-instance environment row, so it reads clearly
 * as an instance of its sandbox template ("Instance of <template>") and is not
 * mistaken for the durable `type='sandbox'` pointer row (which is the one you
 * set as a default). Only `type='ephemeral'` rows with a known backing template
 * get it; every other row returns null. Exported for unit testing.
 */
export function formatInstanceDescriptor(
  env: Pick<EnvironmentItem, 'type' | 'templateName'>
): string | null {
  if (env.type !== 'ephemeral' || !env.templateName) return null;
  return `Instance of ${env.templateName}`;
}

export const formatTimeUntilExpiry = (expiresAt: string | null, nowMs = Date.now()) => {
  if (!expiresAt) return '—';
  const ms = new Date(expiresAt).getTime() - nowMs;
  if (Number.isNaN(ms)) return '—';
  if (ms <= 0) return 'Expired';
  const minutes = Math.floor(ms / 60000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
};

export function formatEnvironmentExpiryLabel(
  env: Pick<EnvironmentItem, 'type' | 'expiresAt'>,
  nowMs = Date.now()
): string | null {
  if (env.type !== 'ephemeral' || !env.expiresAt) return null;
  const remainingMs = new Date(env.expiresAt).getTime() - nowMs;
  return remainingMs <= 0 ? 'Expired' : `Expires in ${formatTimeUntilExpiry(env.expiresAt, nowMs)}`;
}

export function isVisibleEnvironmentListItem(
  item: Pick<EnvironmentItem, 'type' | 'expiresAt'>,
  nowMs = Date.now()
): boolean {
  if (item.type !== 'ephemeral' || !item.expiresAt) return true;
  const expiresMs = new Date(item.expiresAt).getTime();
  if (Number.isNaN(expiresMs)) return true;
  const expiredCutoffMs = nowMs - 24 * 60 * 60 * 1000;
  return expiresMs > expiredCutoffMs;
}

export const formatEnvironmentUpdatedAt = (value: string) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(d);
};

export function getEnvironmentRowPresentation(
  env: Pick<EnvironmentItem, 'type' | 'templateName' | 'expiresAt' | 'updatedAt'>,
  nowMs = Date.now()
) {
  return {
    typeBadge: getEnvironmentTypeBadge(env.type),
    instanceDescriptor: formatInstanceDescriptor(env),
    expiryLabel: formatEnvironmentExpiryLabel(env, nowMs),
    updatedAtLabel: formatEnvironmentUpdatedAt(env.updatedAt),
  };
}
